import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Star, Medal, ArrowLeft, Sparkle, Gift, ChevronDown, Calendar, Timer } from "lucide-react";
import { useParams } from "react-router-dom";
import { getWeeklyLaunchCompanies, addCompanyImpression, getUpcomingLaunches, getPastLaunches } from "../../utils/api";

const LaunchHome = () => {
  const [filter, setFilter] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [name, setName] = useState("");
  const [launchDateStart, setLaunchDateStart] = useState("");
  const [launchDateEnd, setLaunchDateEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [launchId, setLaunchId] = useState("");
  const [animatedScores, setAnimatedScores] = useState({});
  const [upcomingLaunches, setUpcomingLaunches] = useState([]);
  const [sortField, setSortField] = useState('launchDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pastLaunches, setPastLaunches] = useState([]);
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  let imageUrl = process.env.REACT_APP_IMAGE_URL;
  const { id } = useParams(); // Get the ID from the URL params

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Add the timezone offset to get the correct date
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
    return utcDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const data = await getWeeklyLaunchCompanies(id);

        if (data.success) {
          if (data.data != null) {
            setLaunchDateStart(formatDate(data.data.launch_date_start));
            setLaunchDateEnd(formatDate(data.data.launch_date_end));
            setName(data.data.name);
            setLaunchId(data.data.launch_id);
            setStatus(data.data.status);
            setCompanies(data.data.companies);

            // Check last impression time from localStorage
            const lastImpressionTime = localStorage.getItem('lastImpressionTime');
            const currentTime = new Date().getTime();

            // If no previous impression or 24 hours have passed
            if (!lastImpressionTime || (currentTime - parseInt(lastImpressionTime)) > 24 * 60 * 60 * 1000) {
              for (let i = 0; i < data.data.companies.length; i++) {
                let item = data.data.companies[i];
                addCompanyImpression(item.company_id, data.data.name, 'launch', data.data.launch_id);
              }
              // Save current timestamp
              localStorage.setItem('lastImpressionTime', currentTime.toString());
            }
          } else {
            // Fetch upcoming launches if no weekly companies
            const upcomingData = await getUpcomingLaunches();
            if (upcomingData.success) {
              setUpcomingLaunches(upcomingData.data);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPastLaunches = async () => {
      try {
        const data = await getPastLaunches();
        if (data.success) {
          setPastLaunches(data.data);
        }
      } catch (error) {
        console.error("Error fetching past launches:", error);
      }
    };
    
    fetchPastLaunches();
    fetchCompanies();
  }, []);

  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getCountdown = (dateString) => {
    if (!dateString) return '';
    const launchDate = new Date(dateString);
    const now = new Date();
    const diffTime = launchDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Launched';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  useEffect(() => {
    if (companies.length > 0) {
      companies.forEach((competitor) => {
        let startValue = 0;
        const endValue = competitor.activity_score;
        const duration = 1000; // Animation duration in milliseconds
        const framesPerSecond = 60;
        const totalFrames = (duration / 1000) * framesPerSecond;
        const increment = (endValue - startValue) / totalFrames;

        let currentFrame = 0;
        const timer = setInterval(() => {
          if (currentFrame >= totalFrames) {
            clearInterval(timer);
            setAnimatedScores(prev => ({
              ...prev,
              [competitor.company_id]: endValue
            }));
          } else {
            setAnimatedScores(prev => ({
              ...prev,
              [competitor.company_id]: Math.round(startValue + increment * currentFrame)
            }));
            currentFrame++;
          }
        }, 1000 / framesPerSecond);
      });
    }
  }, [companies]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
            <p className="text-lg text-gray-600">Loading launches...</p>
          </div>
        </div>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation */}
          <div className="flex items-center mb-6">
            <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900 mr-2">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Home
            </Link>
            <div className="h-6 w-px bg-gray-300 mx-2" />
            <h1 className="text-3xl font-bold text-gray-900">Weekly Product Launches</h1>
          </div>

          {/* Submit Product CTA */}
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Have a product you'd like to launch? Join our weekly showcase.
              </p>
              <Link
                to="/launch/submit-product"
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Submit Product
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Current Week Competition or Upcoming Launches */}
          {companies.length > 0 ? (
            <div className="mb-12">
              <div className="rounded-lg border-2 border-purple-100 shadow-lg bg-white">
                <div className="p-6 bg-gradient-to-r from-purple-50 to-transparent">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{name} Leaderboard</h2>
                      <p className="text-purple-600 font-medium">
                        {launchDateStart} - {launchDateEnd}
                      </p>
                    </div>

                    {status == 'Running' && (
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-green-500 text-white">
                        LIVE
                      </span>
                    )}

                    {status == 'Active' && (
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-yellow-500 text-white">
                        COMING SOON
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="w-8"></div>
                        <div className="w-12"></div>
                        <div>Company Details</div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center space-x-2 mr-4">
                          <Sparkle className="w-4 h-4" />
                          <span>Activity Score</span>
                        </div>
                        <div className="w-24"></div>
                      </div>
                    </div>
                    
                    {loading == false && companies.map((competitor, index) => (
                      <div
                        key={competitor.company_id}
                        className={`flex items-center justify-between bg-white rounded-lg p-4 transition-all duration-200 hover:shadow-md
                          ${index < 3 ? 'border-2 border-purple-100 shadow-sm' : 'border'}
                          ${index === 0 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : ''}
                          ${index === 1 ? 'bg-gradient-to-r from-gray-50 to-transparent' : ''}
                          ${index === 2 ? 'bg-gradient-to-r from-amber-50 to-transparent' : ''}`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-8 h-8">

                            <span className="text-2xl font-bold text-gray-400">
                              {index + 1}
                            </span>
                          </div>

                          <div className="relative h-12 w-12 rounded-full border-2 border-purple-100 overflow-hidden">
                            {competitor.logo ? (
                              <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + competitor.logo} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                                {competitor.name.substring(0, 2)}
                              </div>
                            )}
                          </div>

                          <div className="max-w-[500px]">
                            <h3 className="font-medium text-lg">{competitor.name}</h3>
                            <p className="text-sm text-gray-600">{competitor.tagline}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold text-gray-700">
                                {competitor.industry_name}
                              </span>

                              {competitor.offer_name && (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                                  <Gift className="w-3 h-3 mr-1" />
                                  {competitor.offer_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="text-right">
                            <div className="flex items-center space-x-2">
                              <Sparkle className="w-5 h-5 text-purple-500" />
                              <div>
                                <p className="font-bold text-lg">
                                  {animatedScores[competitor.company_id] || 0}
                                </p>
                              </div>
                            </div>
                          </div>
                          <Link
                            to={`/company/${competitor.company_id}/${competitor.url_slug}`}
                            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Upcoming Launches</h2>
              <div className="space-y-4">
                 <div className="overflow-x-auto bg-white rounded-lg border shadow">
                        <table className="w-full caption-bottom text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th
                                onClick={() => handleSort('name')}
                                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <span>Product</span>
                                  {sortField === 'name' && (
                                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                                  )}
                                </div>
                              </th>
                              <th
                                onClick={() => handleSort('category')}
                                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <span>Category</span>
                                  {sortField === 'category' && (
                                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                                  )}
                                </div>
                              </th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-gray-500">
                                Description
                              </th>
                              <th
                                onClick={() => handleSort('launchDate')}
                                className="h-12 px-4 text-left align-middle font-medium text-gray-500 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                              >
                                <div className="flex items-center justify-between">
                                  <span>Launch Date</span>
                                  {sortField === 'launchDate' && (
                                    <ChevronDown className={`h-4 w-4 ml-1 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                                  )}
                                </div>
                              </th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-gray-500 whitespace-nowrap">
                                Status
                              </th>
                
                
                
                            </tr>
                          </thead>
                          <tbody>
                            {upcomingLaunches.map((launch) => (
                              <tr
                                key={launch.id}
                                className="border-b hover:bg-gray-50 transition-colors"
                              >
                                <td className="p-4 align-middle">
                                  <div className="flex items-center space-x-3">
                                    <div className="relative h-12 w-12 rounded-full border-2 border-purple-100 overflow-hidden">
                                      {launch.logo ? (
                                        <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + launch.logo} alt="" className="h-full w-full object-cover" />
                                      ) : (
                                        <div className="h-full w-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                                          {launch.name.substring(0, 2)}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <div className="font-semibold">{launch.name}</div>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {launch.featured && (
                                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                                            Featured
                                          </span>
                                        )}
                                        {launch.hasDemo && (
                                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                            Demo
                                          </span>
                                        )}
                                        {launch.hasExclusiveOffer && (
                                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                                            Exclusive
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4 align-middle text-gray-700">
                                  <div>
                                    {launch.industry_name}
                                  </div>
                
                                  {launch.offer_name && (
                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                                      <Gift className="w-3 h-3 mr-1" />
                                      {launch.offer_name}
                                    </span>
                                  )}
                                </td>
                                <td className="p-4 align-middle text-gray-600 line-clamp-2 max-w-xs">
                                  {launch.tagline}
                                </td>
                                <td className="p-4 align-middle whitespace-nowrap">
                                  <div className="flex items-center text-gray-600">
                                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                                    <span>{formatDate(launch.launch_date_start)}</span>
                                  </div>
                                </td>
                                <td className="p-4 align-middle">
                                  <div className="flex items-center text-purple-600 font-medium">
                                    <Timer className="h-4 w-4 mr-2" />
                                    <span>{getCountdown(launch.launch_date_start)}</span>
                                  </div>
                                </td>
                
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
              </div>
            </div>
          )}

          {/* Upcoming Launches Banner - Only show when viewing weekly competition */}
          {companies.length > 0 && (
            <div className="mb-6">
              <Link 
                to="/launch/upcoming"
                className="block bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-4">
                    <Gift className="w-6 h-6" />
                    <div>
                      <h3 className="text-lg font-semibold">Discover Upcoming Product Launches</h3>
                      <p className="text-purple-100">Get early access and exclusive deals</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">View Schedule</span>
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </div>
                </div>
              </Link>
            </div>
          )}
          
          {/* Past Winners Section */}
          {pastLaunches.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Past Winners</h2>
              </div>
              
              {pastLaunches.map((launch) => (
                <div key={launch.launch_id} className="mb-8 rounded-lg border bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-6 bg-gradient-to-r from-gray-50 to-transparent">
                    <h3 className="text-xl font-bold">{launch.name}</h3>
                    <p className="text-sm text-gray-600">
                      {formatDate(launch.launch_date_start)} - {formatDate(launch.launch_date_end)}
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      {launch.companies && launch.companies.slice(0, 3).map((winner, index) => (
                        <div key={winner.company_id} className="flex items-center justify-between bg-white rounded-lg border p-4">
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold w-20 
                              ${index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                                index === 1 ? 'bg-gray-300 text-gray-900' : 
                                'bg-amber-600 text-amber-100'}`}>
                              {index === 0 ? 'Gold' : index === 1 ? 'Silver' : 'Bronze'}
                            </span>
                            <div className="relative h-12 w-12 rounded-full border-2 border-gray-100 overflow-hidden">
                              {winner.logo ? (
                                <img 
                                  src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + winner.logo}
                                  alt={winner.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-medium">
                                  {winner.name.substring(0, 2)}
                                </div>
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium">{winner.name}</h3>
                              <p className="text-sm text-gray-500">{winner.tagline}</p>
                              <p className="text-sm text-gray-600">{winner.industry_name}</p>
                              {winner.offer_name && (
                                <div className="flex flex-col gap-1 mt-2">
                                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 w-fit">
                                    <Gift className="w-3 h-3 mr-1" />
                                    {winner.offer_name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            {/* <div className="text-right">
                              <p className="text-sm text-gray-500">Activity Score</p>
                              <p className="font-medium">{winner.activity_score}</p>
                            </div> */}
                            <Link
                              to={`/company/${winner.company_id}/${winner.url_slug}`}
                              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default LaunchHome;